import { useState, useEffect } from "react";
import { Facebook, Instagram, Twitter, Linkedin, Camera } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useProfile } from "@/hooks/useProfile";
import { useToast } from "@/hooks/use-toast";

interface EditSocialMediaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EditSocialMediaDialog = ({ open, onOpenChange }: EditSocialMediaDialogProps) => {
  const { profile, updateProfile } = useProfile();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [socialLinks, setSocialLinks] = useState({
    facebook_url: "",
    instagram_url: "",
    twitter_url: "",
    linkedin_url: "",
    snapchat_url: "",
  });

  useEffect(() => {
    if (profile) {
      setSocialLinks({
        facebook_url: profile.facebook_url || "",
        instagram_url: profile.instagram_url || "",
        twitter_url: profile.twitter_url || "",
        linkedin_url: profile.linkedin_url || "",
        snapchat_url: profile.snapchat_url || "",
      });
    }
  }, [profile]);

  const handleSave = async () => {
    setSaving(true);
    const { error } = await updateProfile({
      facebook_url: socialLinks.facebook_url || null,
      instagram_url: socialLinks.instagram_url || null,
      twitter_url: socialLinks.twitter_url || null,
      linkedin_url: socialLinks.linkedin_url || null,
      snapchat_url: socialLinks.snapchat_url || null,
    });

    if (error) {
      toast({
        title: "Error",
        description: "Failed to update social media links",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Social media links updated successfully",
      });
      onOpenChange(false);
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Connect Social Media</DialogTitle>
          <DialogDescription>
            Add your social media profile links to let others connect with you
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="facebook" className="flex items-center gap-2">
              <Facebook className="h-4 w-4" />
              Facebook
            </Label>
            <Input
              id="facebook"
              placeholder="https://facebook.com/username"
              value={socialLinks.facebook_url}
              onChange={(e) => setSocialLinks({ ...socialLinks, facebook_url: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="instagram" className="flex items-center gap-2">
              <Instagram className="h-4 w-4" />
              Instagram
            </Label>
            <Input
              id="instagram"
              placeholder="https://instagram.com/username"
              value={socialLinks.instagram_url}
              onChange={(e) => setSocialLinks({ ...socialLinks, instagram_url: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="twitter" className="flex items-center gap-2">
              <Twitter className="h-4 w-4" />
              Twitter
            </Label>
            <Input
              id="twitter"
              placeholder="https://twitter.com/username"
              value={socialLinks.twitter_url}
              onChange={(e) => setSocialLinks({ ...socialLinks, twitter_url: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="linkedin" className="flex items-center gap-2">
              <Linkedin className="h-4 w-4" />
              LinkedIn
            </Label>
            <Input
              id="linkedin"
              placeholder="https://linkedin.com/in/username"
              value={socialLinks.linkedin_url}
              onChange={(e) => setSocialLinks({ ...socialLinks, linkedin_url: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="snapchat" className="flex items-center gap-2">
              <Camera className="h-4 w-4" />
              Snapchat
            </Label>
            <Input
              id="snapchat"
              placeholder="https://snapchat.com/add/username"
              value={socialLinks.snapchat_url}
              onChange={(e) => setSocialLinks({ ...socialLinks, snapchat_url: e.target.value })}
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1"
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} className="flex-1" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditSocialMediaDialog;
